import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Badge
} from '@mui/material';
import {
  LightbulbOutlined,
  FolderOutlined,
  AssessmentOutlined,
  NotificationsOutlined,
  ChatOutlined,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { razorpayService } from '../services/razorpayService';
import { projectService } from '../services/projectService';
import { apiService } from '../services/api';
import Footer from '../components/Footer';
import logo from '../assets/logo.png';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [projectData, setProjectData] = useState({
    name: '',
    type: '',
    investment: '',
    location: '',
    description: '',
    industryType: '',
    services: []
  });

  // Helper function to capitalize username
  const capitalizeUsername = (username) => {
    if (!username) return '';
    return username.charAt(0).toUpperCase() + username.slice(1).toLowerCase();
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userInfo = localStorage.getItem('userInfo');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (userInfo) {
      setUser(JSON.parse(userInfo));
    }

    // Load unread message count
    loadUnreadCount();

    // Poll for new messages every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const loadUnreadCount = async () => {
    try {
      const response = await apiService.getUnreadMessageCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };







  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      navigate('/login');
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) navigate('/projects');
    if (newValue === 2) navigate('/messages');
    if (newValue === 3) navigate('/schemes');
  };

  const handleStartNewProject = () => {
    setOpenProjectDialog(true);
  };

  const handleCreateProject = async (skipPayment = false) => {
    try {
      if (!projectData.name || !projectData.type || !projectData.investment) {
        alert('Please fill in all required fields');
        return;
      }

      const formData = new FormData();
      formData.append('name', projectData.name);
      formData.append('project_type', projectData.type);
      formData.append('investment_amount', parseFloat(projectData.investment.replace(/,/g, '')));
      formData.append('industry_type', (projectData.industryType || '').toLowerCase().replace(' ', '_'));
      formData.append('description', projectData.description);
      formData.append('services', JSON.stringify(projectData.services || []));

      // Create project first
      const response = await projectService.createProject(formData);
      const createdProject = response.data;
      
      // If skipPayment is true (demo mode), just close dialog
      if (skipPayment) {
        alert(`Project "${projectData.name}" created successfully!`);
        setOpenProjectDialog(false);
        setProjectData({ name: '', type: '', investment: '', location: '', description: '', industryType: '', services: [] });
        return;
      }
      
      // Otherwise, initiate payment
      razorpayService.openProjectPayment(
        createdProject.id,
        (verificationResult, razorpayResponse) => {
          // Payment successful
          alert(`Payment successful! Project "${projectData.name}" submitted successfully!`);
          setOpenProjectDialog(false);
          setProjectData({ name: '', type: '', investment: '', location: '', description: '', industryType: '', services: [] });
        },
        (error) => {
          // Payment failed
          alert('Payment failed: ' + (error.message || 'Please try again'));
        }
      );
      
    } catch (error) {
      alert('Failed to create project: ' + error.message);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8f9fa', minHeight: '100vh', pb: 8 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Toolbar sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img src={logo} alt="TICSPL" style={{ height: '32px', marginRight: '12px' }} />
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              TICSPL
            </Typography>
          </Box>
          
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{ 
              flexGrow: 1,
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '16px'
              },
              '& .Mui-selected': {
                color: '#1976d2 !important'
              }
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Projects" />
            <Tab label="Messages" />
            <Tab label="Schemes" />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#666' }} onClick={() => navigate('/messages')}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsOutlined />
              </Badge>
            </IconButton>
            <Button
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ 
                color: '#333',
                textTransform: 'none',
                fontWeight: 500
              }}
              endIcon={<Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>{user?.username?.charAt(0).toUpperCase()}</Avatar>}
            >
              {capitalizeUsername(user?.username) || 'Priya'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 4, mx: 'auto' }}>
        <>
        {/* Welcome Section */}
        <Box sx={{ mb: 4, maxWidth: '1400px' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
            Hello, {capitalizeUsername(user?.username) || 'Priya'} 👋
          </Typography>
          <Typography variant="h5" sx={{ color: '#666', fontWeight: 400 }}>
            Let's get your project started.
          </Typography>
        </Box>

        {/* Main Action Cards - 2x2 Grid */}
        <Grid container spacing={3} sx={{ mb: 4, maxWidth: '1400px' }}>
          {/* Row 1 */}
          <Grid item xs={12} sm={6}>
            <Card sx={{ 
              height: 160, 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: '1px solid #e0e0e0',
              '&:hover': { 
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', 
                transform: 'translateY(-4px)',
                borderColor: '#1976d2'
              } 
            }} onClick={handleStartNewProject}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ 
                    width: 64, 
                    height: 64, 
                    borderRadius: 2, 
                    bgcolor: '#e3f2fd', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2.5,
                    flexShrink: 0
                  }}>
                    <LightbulbOutlined sx={{ color: '#1976d2', fontSize: 36 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                      Start New Project
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      Begin the process by ideating and evaluating your project
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{ 
              height: 160, 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: '1px solid #e0e0e0',
              '&:hover': { 
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', 
                transform: 'translateY(-4px)',
                borderColor: '#1976d2'
              } 
            }} onClick={() => navigate('/projects')}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ 
                    width: 64, 
                    height: 64, 
                    borderRadius: 2, 
                    bgcolor: '#e3f2fd', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2.5,
                    flexShrink: 0
                  }}>
                    <FolderOutlined sx={{ color: '#1976d2', fontSize: 36 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                      My Projects
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      Access and manage all your ongoing projects and DPRs
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12} sm={6}>
            <Card sx={{ 
              height: 160, 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: '1px solid #e0e0e0',
              '&:hover': { 
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', 
                transform: 'translateY(-4px)',
                borderColor: '#1976d2'
              } 
            }} onClick={() => navigate('/schemes')}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ 
                    width: 64, 
                    height: 64, 
                    borderRadius: 2, 
                    bgcolor: '#e3f2fd', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2.5,
                    flexShrink: 0
                  }}>
                    <AssessmentOutlined sx={{ color: '#1976d2', fontSize: 36 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                      Schemes & Subsidies
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      Explore and apply for government schemes and subsidies
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{ 
              height: 160, 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              border: '1px solid #e0e0e0',
              '&:hover': { 
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', 
                transform: 'translateY(-4px)',
                borderColor: '#1976d2'
              } 
            }} onClick={() => navigate('/messages')}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ 
                    width: 64, 
                    height: 64, 
                    borderRadius: 2, 
                    bgcolor: '#e3f2fd', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2.5,
                    flexShrink: 0
                  }}>
                    <ChatOutlined sx={{ color: '#1976d2', fontSize: 36 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                      Talk to Consultant
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      Connect with our industry experts for guidance
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Latest Updates - Separate Section */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, color: '#1a1a1a' }}>
                  Latest Updates
                </Typography>
                <Box sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: '50%', mr: 2, mt: 0.75, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        The deadline for MSME subsidy applications is approaching soon.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        3 days ago
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, bgcolor: '#4caf50', borderRadius: '50%', mr: 2, mt: 0.75, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        New project templates are now available
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        1 week ago
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </>
      </Container>

      {/* Fixed Footer */}
      <Footer />



      {/* Project Creation Dialog */}
      <Dialog open={openProjectDialog} onClose={() => setOpenProjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>Create New Project</Typography>
          <IconButton onClick={() => setOpenProjectDialog(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            {/* Basic Information */}
            <TextField
              fullWidth
              label="Project Name"
              value={projectData.name}
              onChange={(e) => setProjectData({...projectData, name: e.target.value})}
              required
              size="small"
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required size="small">
                <InputLabel>Project Type</InputLabel>
                <Select
                  value={projectData.type}
                  onChange={(e) => setProjectData({...projectData, type: e.target.value})}
                  label="Project Type"
                >
                  <MenuItem value="Food Processing Unit">Food Processing Unit</MenuItem>
                  <MenuItem value="Garment Manufacturing Unit">Garment Manufacturing Unit</MenuItem>
                  <MenuItem value="Fabrication Plant">Fabrication Plant</MenuItem>
                  <MenuItem value="Solar Power Project">Solar Power Project</MenuItem>
                  <MenuItem value="Electronics Manufacturing">Electronics Manufacturing</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth required size="small">
                <InputLabel>Industry Type</InputLabel>
                <Select
                  value={projectData.industryType || ''}
                  onChange={(e) => setProjectData({...projectData, industryType: e.target.value})}
                  label="Industry Type"
                >
                  <MenuItem value="Food Processing">Food Processing</MenuItem>
                  <MenuItem value="Textile">Textile</MenuItem>
                  <MenuItem value="Engineering">Engineering</MenuItem>
                  <MenuItem value="Electronics">Electronics</MenuItem>
                  <MenuItem value="Renewable Energy">Renewable Energy</MenuItem>
                  <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                  <MenuItem value="Agriculture">Agriculture</MenuItem>
                  <MenuItem value="Healthcare">Healthcare</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <TextField
              fullWidth
              label="Total Investment (in Rupees)"
              value={projectData.investment}
              onChange={(e) => setProjectData({...projectData, investment: e.target.value})}
              placeholder="e.g., 30,00,000"
              required
              size="small"
            />
            
            <TextField
              fullWidth
              label="Project Description"
              multiline
              rows={2}
              value={projectData.description}
              onChange={(e) => setProjectData({...projectData, description: e.target.value})}
              placeholder="Brief description of your project..."
              size="small"
            />
            
            {/* Services Selection */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>Services Required</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { value: 'firm_registration', label: 'Firm Registration' },
                  { value: 'udyam_registration', label: 'Udyam Registration' },
                  { value: 'gst_registration', label: 'GST Registration' },
                  { value: 'others', label: 'Others' }
                ].map((service) => (
                  <FormControlLabel
                    key={service.value}
                    control={
                      <Checkbox
                        checked={(projectData.services || []).includes(service.value)}
                        onChange={(e) => {
                          const currentServices = projectData.services || [];
                          const newServices = e.target.checked
                            ? [...currentServices, service.value]
                            : currentServices.filter(s => s !== service.value);
                          setProjectData(prev => ({ ...prev, services: newServices }));
                        }}
                        size="small"
                        sx={{ color: '#1976d2' }}
                      />
                    }
                    label={service.label}
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '0.875rem',
                        color: '#333'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0', flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
          <Box sx={{ 
            p: 2, 
            bgcolor: '#fff3e0', 
            borderRadius: 1, 
            border: '1px solid #ff9800',
            textAlign: 'center'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100', mb: 0.5 }}>
              Project Submission Fee: ₹500
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Demo mode: Free | Live mode: Pay via Razorpay
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpenProjectDialog(false)} sx={{ color: '#666' }}>Cancel</Button>
            <Button 
              variant="outlined" 
              onClick={() => handleCreateProject(true)}
              disabled={!projectData.name || !projectData.type || !projectData.investment}
              sx={{ 
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': { borderColor: '#059669', bgcolor: 'rgba(16, 185, 129, 0.1)' },
                px: 3
              }}
            >
              Create (Demo - Free)
            </Button>
            <Button 
              variant="contained" 
              onClick={() => handleCreateProject(false)}
              disabled={!projectData.name || !projectData.type || !projectData.investment}
              sx={{ 
                bgcolor: '#ff9800',
                '&:hover': { bgcolor: '#f57c00' },
                px: 3
              }}
            >
              Pay ₹500 & Submit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;